import { useEffect, useCallback, MutableRefObject } from 'react';

const refVsElementCheck = (
  el: HTMLElement | null | undefined | MutableRefObject<HTMLElement | null | undefined>,
  target: any // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
  if (el == null) return true; // intentional to catch undefined or null
  // check if el is a ref
  if ('current' in el) {
    return el.current !== target;
  }
  // otherwise, el is an element (likely from document.getElementById)
  return !el?.contains(target);
};

export const useClickOutside: <
  T extends HTMLElement | null | undefined,
  S extends HTMLElement | null | undefined
>({
  ref,
  onClickOutside,
  isOpen,
  exclusion,
}: {
  ref: MutableRefObject<T>;
  onClickOutside: () => void;
  isOpen: boolean;
  exclusion?:
    | MutableRefObject<S>
    | HTMLElement
    | null
    | Array<MutableRefObject<S> | HTMLElement | null>;
}) => void = ({ ref, onClickOutside, isOpen, exclusion }): void => {
  const doesNotHaveExclusion = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (target: any) => {
      if (Array.isArray(exclusion))
        exclusion.some((exclusionEl) => refVsElementCheck(exclusionEl, target));
      return refVsElementCheck(
        exclusion as MutableRefObject<HTMLElement | null> | HTMLElement | null,
        target
      );
    },
    [exclusion]
  );
  const handleClickOutside = useCallback(
    (e) => {
      if (!ref?.current?.contains(e.target) && doesNotHaveExclusion(e.target)) {
        onClickOutside();
      }
    },
    [ref, onClickOutside, doesNotHaveExclusion]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);
};

export default useClickOutside;
